import React, { useState, useEffect } from 'react';
import { Check, Microscope, Upload, FileText, PawPrint, User, Sparkles, Shield, Clock, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = ({ onGetStarted, userId, credits }) => {
  const [loading, setLoading] = useState(null);

  const handlePurchase = async (packageId) => {
    if (packageId === 'free') {
      onGetStarted();
      return;
    }
    
    setLoading(packageId);
    try {
      const response = await axios.post(`${API}/checkout/create`, {
        package_id: packageId,
        origin_url: window.location.origin,
        user_id: userId
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      } else if (response.data.success) {
        onGetStarted();
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0D2340] via-[#0f3460] to-[#1a1a2e]">
      {/* Header */}
      <header className="py-6 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl">🔬</span>
            <span className="text-xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              Hikari Lab Reader
            </span>
          </div>
          {credits > 0 && (
            <button
              onClick={onGetStarted}
              className="px-4 py-2 bg-[#5eead4] text-[#0D2340] rounded-lg font-semibold text-sm hover:bg-[#2dd4bf] transition-all"
            >
              Open App ({credits} credits)
            </button>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4 text-center">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Understand Your Lab Results
            <span className="block text-[#5eead4]">In Plain Language</span>
          </h1>
          <p className="text-lg sm:text-xl text-white/60 mb-8 max-w-2xl mx-auto">
            Upload your lab report or enter values manually. Get instant AI-powered interpretations — for humans and pets.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => handlePurchase('free')}
              className="px-8 py-4 bg-[#5eead4] text-[#0D2340] rounded-xl font-bold text-lg hover:bg-[#2dd4bf] transition-all flex items-center justify-center gap-2"
              data-testid="try-free-btn"
            >
              Try Free <ArrowRight className="w-5 h-5" />
            </button>
            <a
              href="#pricing"
              className="px-8 py-4 bg-white/10 text-white rounded-xl font-semibold text-lg hover:bg-white/20 transition-all border border-white/20"
            >
              View Pricing
            </a>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12" style={{ fontFamily: 'Playfair Display, serif' }}>
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Upload className="w-8 h-8" />,
                title: "Upload or Enter",
                description: "Upload a PDF, snap a photo, or enter values manually from 14+ lab panels"
              },
              {
                icon: <Sparkles className="w-8 h-8" />,
                title: "AI Analyzes",
                description: "Claude AI extracts values and checks them against reference ranges"
              },
              {
                icon: <FileText className="w-8 h-8" />,
                title: "Get Clarity",
                description: "Receive plain-language explanations of what your results mean"
              }
            ].map((feature, idx) => (
              <div key={idx} className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center hover:border-[#5eead4]/50 transition-all">
                <div className="w-16 h-16 bg-[#5eead4]/20 rounded-full flex items-center justify-center mx-auto mb-4 text-[#5eead4]">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-white/50">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Human + Pets */}
      <section className="py-16 px-4 bg-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                For Humans & Pets
              </h2>
              <p className="text-white/60 mb-6">
                The only lab reader that handles both human AND veterinary results. Whether it's your annual bloodwork or your dog's senior panel, we've got you covered.
              </p>
              <div className="space-y-4">
                {[
                  { icon: <User className="w-5 h-5" />, text: "Human: CBC, Metabolic, Lipid, Thyroid, HbA1c, Hormones & more" },
                  { icon: <PawPrint className="w-5 h-5" />, text: "Pets: Dog CBC, Cat Metabolic, Horse Panel & more" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#5eead4]/20 rounded-full flex items-center justify-center text-[#5eead4] flex-shrink-0">
                      {item.icon}
                    </div>
                    <span className="text-white/80">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-2xl p-6 text-center">
                <span className="text-4xl mb-2 block">👤</span>
                <span className="text-white font-semibold">Human Labs</span>
              </div>
              <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 rounded-2xl p-6 text-center">
                <span className="text-4xl mb-2 block">🐕</span>
                <span className="text-white font-semibold">Dog Labs</span>
              </div>
              <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-2xl p-6 text-center">
                <span className="text-4xl mb-2 block">🐈</span>
                <span className="text-white font-semibold">Cat Labs</span>
              </div>
              <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 border border-green-500/30 rounded-2xl p-6 text-center">
                <span className="text-4xl mb-2 block">🐴</span>
                <span className="text-white font-semibold">Horse Labs</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Simple, Pay-As-You-Go Pricing
          </h2>
          <p className="text-white/50 text-center mb-12">
            No subscriptions. Buy credits when you need them.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Free */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-[#5eead4]/50 transition-all">
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-white mb-2">Free Trial</h3>
                <div className="text-4xl font-bold text-white mb-1">$0</div>
                <p className="text-white/40 text-sm">1 report included</p>
              </div>
              <ul className="space-y-3 mb-6">
                {["1 lab report", "Upload or manual entry", "AI interpretation", "Works for human or pet"].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-white/70 text-sm">
                    <Check className="w-4 h-4 text-[#5eead4]" />
                    {item}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handlePurchase('free')}
                disabled={loading === 'free'}
                className="w-full py-3 bg-white/10 text-white rounded-xl font-semibold hover:bg-white/20 transition-all border border-white/20"
                data-testid="buy-free"
              >
                {loading === 'free' ? 'Loading...' : 'Get Started Free'}
              </button>
            </div>

            {/* 2 Reports */}
            <div className="bg-[#5eead4]/10 border-2 border-[#5eead4] rounded-2xl p-6 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#5eead4] text-[#0D2340] px-3 py-1 rounded-full text-xs font-bold">
                POPULAR
              </div>
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-white mb-2">2 Reports</h3>
                <div className="text-4xl font-bold text-[#5eead4] mb-1">$2.99</div>
                <p className="text-white/40 text-sm">$1.50 per report</p>
              </div>
              <ul className="space-y-3 mb-6">
                {["2 lab reports", "Upload or manual entry", "AI interpretation", "Works for human or pet", "Never expires"].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-white/70 text-sm">
                    <Check className="w-4 h-4 text-[#5eead4]" />
                    {item}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handlePurchase('pack_2')}
                disabled={loading === 'pack_2'}
                className="w-full py-3 bg-[#5eead4] text-[#0D2340] rounded-xl font-bold hover:bg-[#2dd4bf] transition-all"
                data-testid="buy-pack-2"
              >
                {loading === 'pack_2' ? 'Loading...' : 'Buy Now'}
              </button>
            </div>

            {/* 4 Reports */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-[#5eead4]/50 transition-all">
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-white mb-2">4 Reports</h3>
                <div className="text-4xl font-bold text-white mb-1">$4.99</div>
                <p className="text-white/40 text-sm">$1.25 per report <span className="text-[#5eead4]">• Save 17%</span></p>
              </div>
              <ul className="space-y-3 mb-6">
                {["4 lab reports", "Upload or manual entry", "AI interpretation", "Works for human or pet", "Never expires", "Best value"].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-white/70 text-sm">
                    <Check className="w-4 h-4 text-[#5eead4]" />
                    {item}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handlePurchase('pack_4')}
                disabled={loading === 'pack_4'}
                className="w-full py-3 bg-white/10 text-white rounded-xl font-semibold hover:bg-white/20 transition-all border border-white/20"
                data-testid="buy-pack-4"
              >
                {loading === 'pack_4' ? 'Loading...' : 'Buy Now'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-16 px-4 bg-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[
              { icon: <Shield className="w-6 h-6" />, title: "Privacy First", desc: "Your data is processed securely and never used for AI training" },
              { icon: <Clock className="w-6 h-6" />, title: "Instant Results", desc: "Get interpretations in seconds, not days" },
              { icon: <Microscope className="w-6 h-6" />, title: "Educational", desc: "Learn what your results mean, not a replacement for medical advice" }
            ].map((item, idx) => (
              <div key={idx}>
                <div className="w-12 h-12 bg-[#5eead4]/20 rounded-full flex items-center justify-center mx-auto mb-3 text-[#5eead4]">
                  {item.icon}
                </div>
                <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                <p className="text-white/50 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🔬</span>
              <span className="text-white font-semibold">Hikari Lab Reader</span>
            </div>
            <div className="flex gap-6 text-white/50 text-sm">
              <a href="/privacy" className="hover:text-white transition-colors">Privacy</a>
              <a href="/terms" className="hover:text-white transition-colors">Terms</a>
              <a href="/disclaimer" className="hover:text-white transition-colors">Medical Disclaimer</a>
            </div>
          </div>
          <div className="mt-8 text-center text-white/30 text-xs">
            ⚕️ For educational purposes only · Not medical advice · Consult a qualified healthcare provider or veterinarian
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
